import { useDispatch, useSelector } from 'react-redux';

import RolePermissionService from '../services/RolePermissionService';
import useAlerts from './useAlerts';
import useLoading from './useLoading';
import {
  setRolePermissions,
  togglePermission,
  setRolePermissionList,
} from '../store/slices/rolePermissions/rolePermissionsSlice';
import type { RootState, AppDispatch } from '../store/store';

/**
 * Panel administrativo rol -> permisos (Etapa 4).
 *
 * El endpoint `/api/role-permissions` es solo-SUPPORT. La respuesta trae ahora
 * un `catalog` con metadatos (modulo/accion/label/description/supportOnly) para
 * agrupar por modulo, y `lockedRoles` (SUPPORT) que la UI no deja editar.
 *
 * `updateRolePermissions(role)` envia SOLO el rol editado — no toda la matriz —
 * para no pisar cambios de otros roles por accidente.
 */
export const useRolePermissions = () => {
  const { show: showAlert } = useAlerts();
  const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
  const dispatch = useDispatch<AppDispatch>();

  const { roles, permissions, rolePermissions, catalog, lockedRoles } =
    useSelector((state: RootState) => state.rolePermissions);

  const getRolePermissions = async () => {
    try {
      showLoadingIndicator();
      const result = await RolePermissionService.getRolePermissions();

      const data = result?.data;
      if (!data || !Array.isArray(data.roles) || !Array.isArray(data.permissions)) {
        throw new Error('Respuesta invalida del servidor.');
      }

      dispatch(
        setRolePermissions({
          roles: data.roles,
          permissions: data.permissions,
          rolePermissions: data.rolePermissions ?? {},
          catalog: data.catalog ?? [],
          lockedRoles: data.lockedRoles ?? [],
        }),
      );
    } catch (error) {
      console.error('Error fetching role permissions:', error);
      showAlert('error', 'Error', 'No se pudieron cargar los permisos.');
    } finally {
      hideLoadingIndicator();
    }
  };

  const updateRolePermissions = async (role: string) => {
    try {
      showLoadingIndicator();
      await RolePermissionService.updateRolePermissions({
        rolePermissions: { [role]: rolePermissions[role] ?? [] },
      });
      showAlert('success', 'Guardado', 'Los permisos del rol se actualizaron.');
      return true;
    } catch (error: any) {
      console.error('Error updating permissions:', error);
      const msg =
        error?.response?.data?.message ??
        error?.response?.data?.title ??
        'No se pudieron actualizar los permisos.';
      showAlert('error', 'Error', msg);
      return false;
    } finally {
      hideLoadingIndicator();
    }
  };

  const toggleRolePermission = (role: string, permission: string) => {
    dispatch(togglePermission({ role, permission }));
  };

  const setRolePermissionsForRole = (role: string, perms: string[]) => {
    dispatch(setRolePermissionList({ role, permissions: perms }));
  };

  return {
    roles,
    permissions,
    rolePermissions,
    catalog,
    lockedRoles,
    getRolePermissions,
    updateRolePermissions,
    toggleRolePermission,
    setRolePermissionsForRole,
  };
};

export default useRolePermissions;
